/** Properties of the selection (element or audio clip), or project settings when nothing is selected. */

import { clipDuration, elementTimes, frameRect, sortedScenes, type AudioClip, type CameraKey, type Element, type Project, type Scene } from "@studio/engine";
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalSpaceAround,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalSpaceAround,
  ArrowDownToLine,
  ArrowUpToLine,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  Lock,
  Mic,
  Trash2,
  Unlock,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { Draft } from "immer";
import { addCameraKey, align, deleteScene, deleteSelection, distribute, duplicateSelection, overrideAutoKey, reorder } from "./actions";
import { queueDiffFor } from "./autoplace";
import { flush } from "./save";
import { api } from "../api";
import type { Preview } from "./preview";
import { useEditor } from "./store";
import { Color, NumberField, Row, Select, TextArea } from "./ui";

export function Inspector({ preview }: { preview: Preview }) {
  const project = useEditor((s) => s.project)!;
  const selection = useEditor((s) => s.selection);
  const multi = useEditor((s) => s.multi);
  if (selection?.kind === "element" && multi.length > 0) return <MultiInspector preview={preview} count={multi.length + 1} />;
  if (selection?.kind === "scene") {
    const sc = project.scenes.find((x) => x.id === selection.id);
    if (sc) return <SceneInspector scene={sc} project={project} />;
  }
  if (selection?.kind === "camkey") {
    const k = project.camera.keyframes.find((x) => x.id === selection.id) ?? preview.cameraKeys().auto.find((x) => x.id === selection.id);
    if (k) return <CameraKeyInspector k={k} project={project} />;
  }
  if (selection?.kind === "marker") {
    const m = project.markers.find((x) => x.id === selection.id);
    if (m)
      return (
        <div className="inspector">
          <div className="insp-head">
            <strong className="name">Marker</strong>
            <div className="icon-row"><button title="Delete (Del)" onClick={deleteSelection}><Trash2 size={15} /></button></div>
          </div>
          <section>
            <Row label="Label">
              <input value={m.label} onChange={(ev) => useEditor.getState().edit((d) => void (d.markers.find((x) => x.id === m.id)!.label = ev.target.value))} />
            </Row>
            <NumberField label="Time" suffix="s" value={m.t} step={1 / project.output.fps} min={0} digits={3}
              onChange={(v) => useEditor.getState().edit((d) => void (d.markers.find((x) => x.id === m.id)!.t = v))} />
            <p className="muted small">Element and clip edges snap to markers while you drag them.</p>
          </section>
        </div>
      );
  }
  if (selection?.kind === "element") {
    const el = project.elements.find((e) => e.id === selection.id);
    if (el) return <ElementInspector el={el} project={project} />;
  }
  if (selection?.kind === "clip") {
    const c = project.audio.find((x) => x.id === selection.id);
    if (c) return <ClipInspector clip={c} project={project} />;
  }
  return <ProjectInspector project={project} preview={preview} />;
}

function SceneInspector({ scene, project }: { scene: Scene; project: Project }) {
  const e = (fn: (d: Draft<Scene>) => void) =>
    useEditor.getState().edit((p) => {
      const sc = p.scenes.find((x) => x.id === scene.id);
      if (sc) fn(sc);
    });
  const first = sortedScenes(project)[0]?.id === scene.id;
  const fr = frameRect(project, scene);
  const count = project.elements.filter((el) => el.sceneId === scene.id).length;
  return (
    <div className="inspector">
      <div className="insp-head">
        <input className="name" value={scene.name} placeholder="Scene name" onChange={(ev) => e((d) => void (d.name = ev.target.value))} />
        <div className="icon-row"><button title="Delete scene (its elements join the previous scene)" onClick={() => deleteScene(scene.id)}><Trash2 size={15} /></button></div>
      </div>
      <section>
        <Row label="Number">
          <input value={scene.number ?? ""} placeholder="e.g. 32" onChange={(ev) => e((d) => void (d.number = ev.target.value || null))} />
        </Row>
        {!first && <NumberField label="Starts at" suffix="s" value={scene.start} step={0.1} min={0} onChange={(v) => e((d) => void (d.start = v))} />}
        <p className="muted small">{count} element{count === 1 ? "" : "s"}. Element times are relative to the scene start, so moving the scene moves them too.</p>
      </section>
      {!first && (
        <section>
          <h4>Start</h4>
          <Row label="Board">
            <Select value={scene.startMode} options={[["clear", "Clear the board"], ["build", "Build on the previous scene"]]} onChange={(v) => e((d) => void (d.startMode = v))} />
          </Row>
          <Row label="Transition">
            <Select
              value={scene.transition.type}
              options={[["cut", "Cut"], ["wipe", "Wipe"], ["erase", "Erase (hand with eraser)"], ["pan", "Camera pan to this scene's frame"]]}
              onChange={(v) => e((d) => {
                d.transition.type = v;
                if (v !== "cut" && d.transition.duration <= 0) d.transition.duration = v === "pan" ? 1.5 : 1;
                if (v === "pan" && !d.frame) {
                  const prev = frameRect(project, sortedScenes(project).filter((x) => x.start < scene.start).pop());
                  d.frame = { ...prev, x: prev.x + prev.w };
                }
              })}
            />
          </Row>
          {scene.transition.type !== "cut" && (
            <NumberField label="Duration" suffix="s" value={scene.transition.duration} step={0.05} min={0.1} onChange={(v) => e((d) => void (d.transition.duration = v))} />
          )}
          {scene.transition.type === "wipe" && (
            <Row label="Direction">
              <Select value={scene.transition.direction} options={[["right", "Left → right"], ["left", "Right → left"], ["down", "Top → bottom"], ["up", "Bottom → top"]]}
                onChange={(v) => e((d) => void (d.transition.direction = v))} />
            </Row>
          )}
          <p className="muted small">
            {scene.transition.type === "pan"
              ? "Earlier content stays on the board; the camera travels to this scene's frame. Place frames in board view (B)."
              : scene.startMode === "build"
                ? "The previous scene's content stays; this scene adds to it."
                : "Everything from earlier scenes is removed when this scene starts. Give elements a start ≥ the transition length."}
          </p>
        </section>
      )}
      <section>
        <h4>Frame on the board</h4>
        <div className="grid2">
          <NumberField label="X" value={fr.x} step={10} digits={0} onChange={(v) => e((d) => void (d.frame = { ...fr, x: v }))} />
          <NumberField label="Y" value={fr.y} step={10} digits={0} onChange={(v) => e((d) => void (d.frame = { ...fr, y: v }))} />
        </div>
        <NumberField label="Width" value={fr.w} step={10} min={192} digits={0}
          onChange={(v) => e((d) => void (d.frame = { ...fr, w: v, h: Math.round((v * project.output.height) / project.output.width) }))} />
        <p className="muted small">Drag and resize frames in board view (<kbd>B</kbd>).</p>
      </section>
      <section>
        <h4>Caption</h4>
        <TextArea value={scene.caption ?? ""} onChange={(v) => e((d) => void (d.caption = v || null))} />
        {!project.captions.enabled && <p className="muted small">Captions are off. Turn them on in project settings (click empty stage).</p>}
      </section>
    </div>
  );
}

function CameraKeyInspector({ k, project }: { k: CameraKey; project: Project }) {
  const manual = project.camera.keyframes.some((x) => x.id === k.id);
  const e = (fn: (d: Draft<CameraKey>) => void) =>
    useEditor.getState().edit((p) => {
      const key = p.camera.keyframes.find((x) => x.id === k.id);
      if (key) fn(key);
    });
  return (
    <div className="inspector">
      <div className="insp-head">
        <strong className="name">{manual ? "Camera key" : "Auto-camera key"}</strong>
        {manual && <div className="icon-row"><button title="Delete (Del)" onClick={deleteSelection}><Trash2 size={15} /></button></div>}
      </div>
      {!manual && (
        <section>
          <p className="muted small">Generated by auto-camera. Override it to edit; the override replaces just this key.</p>
          <button className="wide" onClick={() => overrideAutoKey(k)}>Override this key</button>
        </section>
      )}
      <section className={manual ? "" : "disabled"}>
        <NumberField label="Time" suffix="s" value={k.t} step={0.05} min={0} onChange={(v) => e((d) => void (d.t = v))} />
        <div className="grid2">
          <NumberField label="X" value={k.x} step={5} digits={0} onChange={(v) => e((d) => void (d.x = v))} />
          <NumberField label="Y" value={k.y} step={5} digits={0} onChange={(v) => e((d) => void (d.y = v))} />
          <NumberField label="Zoom" value={k.zoom} step={0.02} min={0.1} max={10} digits={3} onChange={(v) => e((d) => void (d.zoom = v))} />
          <NumberField label="Rotate" suffix="°" value={k.rotation} step={0.5} digits={1} onChange={(v) => e((d) => void (d.rotation = v))} />
        </div>
        <Row label="Move in">
          <Select value={k.easing} options={[["easeInOut", "Ease in-out"], ["easeOut", "Ease out"], ["easeIn", "Ease in"], ["linear", "Linear"], ["hold", "Cut (jump)"]]}
            onChange={(v) => e((d) => void (d.easing = v))} />
        </Row>
        <p className="muted small">Easing shapes the move into this key from the previous one. Drag/resize the orange frame in board view (<kbd>B</kbd>).</p>
      </section>
    </div>
  );
}

function editEl(id: string, fn: (d: Draft<Element>) => void) {
  useEditor.getState().edit((p) => {
    const el = p.elements.find((e) => e.id === id);
    if (el) fn(el);
  });
}

const DIRS: [Element["enter"]["direction"], string][] = [
  ["left", "Left"],
  ["right", "Right"],
  ["top", "Top"],
  ["bottom", "Bottom"],
];

function ElementInspector({ el, project }: { el: Element; project: Project }) {
  const e = (fn: (d: Draft<Element>) => void) => editEl(el.id, fn);
  const tm = elementTimes(project).get(el.id)!;
  const draws = el.enter.mode === "draw" || el.enter.mode === "outline";
  const hasFill = el.enter.mode === "draw" && (el.type === "image" || !!el.shape?.fill);
  const hands = project.assets.filter((a) => a.kind === "hand");
  return (
    <div className="inspector">
      <div className="insp-head">
        <input className="name" value={el.name} placeholder={el.type} onChange={(ev) => e((d) => void (d.name = ev.target.value))} />
        <div className="icon-row">
          <button title={el.hidden ? "Show" : "Hide"} onClick={() => e((d) => void (d.hidden = !d.hidden))}>
            {el.hidden ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
          <button title={el.locked ? "Unlock" : "Lock position"} onClick={() => e((d) => void (d.locked = !d.locked))}>
            {el.locked ? <Lock size={15} /> : <Unlock size={15} />}
          </button>
          <button title="Duplicate (Ctrl+D)" onClick={duplicateSelection}>
            <Copy size={15} />
          </button>
          <button title="Delete (Del)" onClick={deleteSelection}>
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      <section>
        <h4>Entrance</h4>
        <Row label="Mode">
          <Select
            value={el.enter.mode}
            options={[
              ["draw", el.type === "text" ? "Write" : "Draw (outline, then colour)"],
              ["outline", "Outline only"],
              ["fade", "Fade"],
              ["moveIn", "Move in (hand carries it)"],
              ["instant", "Instant"],
            ]}
            onChange={(v) => e((d) => void (d.enter.mode = v))}
          />
        </Row>
        <NumberField label="Start" suffix="s" value={tm.enter0} step={0.05} min={0} hint="Absolute time; stored relative to the scene"
          onChange={(v) => e((d) => void (d.start = Math.max(0, +(d.start + v - tm.enter0).toFixed(4))))} />
        {draws && <NumberField label={el.type === "text" ? "Write" : "Outline"} suffix="s" value={el.enter.outlineDur} step={0.05} min={0.05} onChange={(v) => e((d) => void (d.enter.outlineDur = v))} />}
        {hasFill && <NumberField label="Colour" suffix="s" value={el.enter.fillDur} step={0.05} min={0.05} onChange={(v) => e((d) => void (d.enter.fillDur = v))} />}
        {(el.enter.mode === "fade" || el.enter.mode === "moveIn") && (
          <NumberField label="Duration" suffix="s" value={el.enter.duration} step={0.05} min={0.05} onChange={(v) => e((d) => void (d.enter.duration = v))} />
        )}
        {el.enter.mode === "moveIn" && (
          <Row label="From">
            <Select value={el.enter.direction} options={DIRS} onChange={(v) => e((d) => void (d.enter.direction = v))} />
          </Row>
        )}
        {(draws || el.enter.mode === "moveIn") && (
          <Row label="Hand">
            <Select
              value={el.enter.handId ?? ""}
              options={[["", "Project default"], ["marker", "Marker"], ["none", "No hand"], ...hands.map((h) => [h.id, h.name] as [string, string])]}
              onChange={(v) => e((d) => void (d.enter.handId = v || null))}
            />
          </Row>
        )}
      </section>

      <section>
        <h4>Hold & exit</h4>
        <Row label="Exit">
          <Select
            value={el.exit.mode}
            options={[
              ["none", "None (stay until cleared)"],
              ["fade", "Fade out"],
              ["moveOff", "Move off"],
              ["erase", "Erase"],
            ]}
            onChange={(v) => e((d) => {
              d.exit.mode = v;
              if (v !== "none" && d.hold === null) d.hold = 2;
            })}
          />
        </Row>
        {el.exit.mode !== "none" && (
          <>
            <NumberField label="Hold" suffix="s" value={el.hold ?? 0} step={0.1} min={0} onChange={(v) => e((d) => void (d.hold = v))} />
            <NumberField label="Exit dur." suffix="s" value={el.exit.duration} step={0.05} min={0.05} onChange={(v) => e((d) => void (d.exit.duration = v))} />
            {el.exit.mode === "moveOff" && (
              <Row label="Towards">
                <Select value={el.exit.direction} options={DIRS} onChange={(v) => e((d) => void (d.exit.direction = v))} />
              </Row>
            )}
          </>
        )}
      </section>

      {(el.image || el.name.startsWith("Placeholder")) && <ReplaceImage el={el} project={project} />}
      {el.text && <TextSection el={el} project={project} />}
      {el.image && (
        <section>
          <h4>Image</h4>
          <NumberField label="Brush" suffix="×" value={el.image.brushScale} step={0.05} min={0.3} max={4} hint="Reveal brush width (raise if thick shapes appear late)"
            onChange={(v) => e((d) => void (d.image!.brushScale = v))} />
          <button className="wide" onClick={() => useEditor.getState().set({ panel: { analysis: el.image!.assetId } })}>
            Outline detection…
          </button>
          <BuildStepControl el={el} project={project} />
          <button className="wide" onClick={() => useEditor.getState().set({ panel: { strokes: el.id } })}>
            Stroke order{el.image.groupOrder ? " (manual)" : ""}…
          </button>
        </section>
      )}
      {el.shape && (
        <section>
          <h4>Shape</h4>
          {(el.shape.kind === "rect" || el.shape.kind === "ellipse") && (
            <>
              <NumberField label="Width" value={el.shape.w} step={2} min={4} digits={0} onChange={(v) => e((d) => void (d.shape!.w = v))} />
              <NumberField label="Height" value={el.shape.h} step={2} min={4} digits={0} onChange={(v) => e((d) => void (d.shape!.h = v))} />
            </>
          )}
          {(el.shape.kind === "line" || el.shape.kind === "arrow") && (
            <NumberField label="Length" value={Math.abs((el.shape.points[1]?.x ?? 180) - (el.shape.points[0]?.x ?? -180))} step={2} min={10} digits={0}
              onChange={(v) => e((d) => void (d.shape!.points = [{ x: -v / 2, y: 0 }, { x: v / 2, y: 0 }]))} />
          )}
          <Row label="Line">
            <Color value={el.shape.stroke} onChange={(v) => e((d) => void (d.shape!.stroke = v))} />
          </Row>
          <NumberField label="Thickness" value={el.shape.strokeWidth} step={0.5} min={1} max={60} onChange={(v) => e((d) => void (d.shape!.strokeWidth = v))} />
          {(el.shape.kind === "rect" || el.shape.kind === "ellipse") && (
            <Row label="Fill">
              <span className="color">
                <input type="checkbox" checked={!!el.shape.fill} onChange={(ev) => e((d) => void (d.shape!.fill = ev.target.checked ? "#f2c14e" : null))} />
                {el.shape.fill && <input type="color" value={el.shape.fill} onChange={(ev) => e((d) => void (d.shape!.fill = ev.target.value))} />}
              </span>
            </Row>
          )}
        </section>
      )}

      <section>
        <h4>Position</h4>
        <div className="grid2">
          <NumberField label="X" value={el.transform.x} step={1} digits={1} onChange={(v) => e((d) => void (d.transform.x = v))} />
          <NumberField label="Y" value={el.transform.y} step={1} digits={1} onChange={(v) => e((d) => void (d.transform.y = v))} />
          <NumberField label="Scale" value={el.transform.scale} step={0.01} min={0.01} digits={3} onChange={(v) => e((d) => void (d.transform.scale = v))} />
          <NumberField label="Rotate" suffix="°" value={el.transform.rotation} step={1} digits={1} onChange={(v) => e((d) => void (d.transform.rotation = v))} />
        </div>
        <NumberField label="Opacity" value={el.opacity} step={0.01} min={0} max={1} onChange={(v) => e((d) => void (d.opacity = v))} />
        <Row label="Flip">
          <span className="checks">
            <label>
              <input type="checkbox" checked={el.transform.flipX} onChange={(ev) => e((d) => void (d.transform.flipX = ev.target.checked))} /> H
            </label>
            <label>
              <input type="checkbox" checked={el.transform.flipY} onChange={(ev) => e((d) => void (d.transform.flipY = ev.target.checked))} /> V
            </label>
          </span>
        </Row>
        <Row label="Layer">
          <span className="icon-row">
            <button title="To back" onClick={() => reorder(el.id, "bottom")}><ArrowDownToLine size={14} /></button>
            <button title="Backward" onClick={() => reorder(el.id, "down")}><ChevronDown size={14} /></button>
            <button title="Forward" onClick={() => reorder(el.id, "up")}><ChevronUp size={14} /></button>
            <button title="To front" onClick={() => reorder(el.id, "top")}><ArrowUpToLine size={14} /></button>
          </span>
        </Row>
      </section>
    </div>
  );
}

function TextSection({ el, project }: { el: Element; project: Project }) {
  const t = el.text!;
  const e = (fn: (d: Draft<Element>) => void) => editEl(el.id, fn);
  const fonts = project.assets.filter((a) => a.kind === "font");
  return (
    <section>
      <h4>Text</h4>
      <TextArea value={t.content} onChange={(v) => e((d) => void (d.text!.content = v))} />
      <Row label="Font">
        <Select value={t.fontAssetId} options={fonts.map((f) => [f.id, f.name] as [string, string])} onChange={(v) => e((d) => void (d.text!.fontAssetId = v))} />
      </Row>
      <NumberField label="Size" value={t.size} step={1} min={8} digits={0} onChange={(v) => e((d) => void (d.text!.size = v))} />
      <Row label="Colour">
        <Color value={t.color} onChange={(v) => e((d) => void (d.text!.color = v))} />
      </Row>
      <Row label="Align">
        <Select value={t.align} options={[["left", "Left"], ["center", "Centre"], ["right", "Right"]]} onChange={(v) => e((d) => void (d.text!.align = v))} />
      </Row>
      <NumberField label="Line height" value={t.lineHeight} step={0.05} min={0.6} max={3} onChange={(v) => e((d) => void (d.text!.lineHeight = v))} />
      <Row label="Draw-on">
        <Select value={t.writeMode} options={[["write", "Write stroke by stroke"], ["wipe", "Wipe in"]]} onChange={(v) => e((d) => void (d.text!.writeMode = v))} />
      </Row>
      <Row label="Underline">
        <span className="color">
          <input type="checkbox" checked={!!t.underline} onChange={(ev) => e((d) => void (d.text!.underline = ev.target.checked ? { color: "#345ca0", width: 9, offset: 18, tilt: -8 } : null))} />
          {t.underline && <input type="color" value={t.underline.color} onChange={(ev) => e((d) => void (d.text!.underline!.color = ev.target.value))} />}
        </span>
      </Row>
    </section>
  );
}

function ClipInspector({ clip, project }: { clip: AudioClip; project: Project }) {
  const e = (fn: (d: Draft<AudioClip>) => void) =>
    useEditor.getState().edit((p) => {
      const c = p.audio.find((x) => x.id === clip.id);
      if (c) fn(c);
    });
  const asset = project.assets.find((a) => a.id === clip.assetId);
  return (
    <div className="inspector">
      <div className="insp-head">
        <strong className="name">{asset?.name}</strong>
        <div className="icon-row">
          <button title="Duplicate after (Ctrl+D)" onClick={duplicateSelection}><Copy size={15} /></button>
          <button title="Remove (Del)" onClick={deleteSelection}><Trash2 size={15} /></button>
        </div>
      </div>
      <section>
        <Row label="Role">
          <Select value={clip.role} options={[["voice", "Voiceover"], ["music", "Music (ducks under voice)"]]} onChange={(v) => e((d) => void (d.role = v))} />
        </Row>
        <NumberField label="Start" suffix="s" value={clip.start} step={0.05} min={0} onChange={(v) => e((d) => void (d.start = v))} />
        <NumberField label="Track" value={clip.track + 1} step={1} min={1} digits={0} onChange={(v) => e((d) => void (d.track = Math.round(v) - 1))} />
        <NumberField label="Volume" value={clip.volume} step={0.01} min={0} max={2} onChange={(v) => e((d) => void (d.volume = v))} />
        <NumberField label="Fade in" suffix="s" value={clip.fadeIn} step={0.05} min={0} onChange={(v) => e((d) => void (d.fadeIn = v))} />
        <NumberField label="Fade out" suffix="s" value={clip.fadeOut} step={0.05} min={0} onChange={(v) => e((d) => void (d.fadeOut = v))} />
        <NumberField label="Trim in" suffix="s" value={clip.trimIn} step={0.05} min={0} onChange={(v) => e((d) => void (d.trimIn = v))} />
        <p className="muted">Length {clipDuration(project, clip).toFixed(2)} s of {asset?.duration?.toFixed(2)} s</p>
        {asset && (
          <button className="wide" onClick={() => useEditor.getState().set({ panel: { voice: asset.id } })} title="Re-speak these words with a built-in voice">
            <Mic size={14} /> Change voice…
          </button>
        )}
      </section>
    </div>
  );
}

function ProjectInspector({ project, preview }: { project: Project; preview: Preview }) {
  const e = (fn: (d: Draft<Project>) => void) => useEditor.getState().edit(fn);
  const hands = project.assets.filter((a) => a.kind === "hand");
  const fonts = project.assets.filter((a) => a.kind === "font");
  return (
    <div className="inspector">
      <section>
        <h4>Project</h4>
        <Row label="Name">
          <input value={project.name} onChange={(ev) => e((d) => void (d.name = ev.target.value))} />
        </Row>
        <Row label="Background">
          <Select
            value={project.board.background.kind}
            options={[["color", "Plain colour"], ["whiteboard", "Whiteboard"], ["chalkboard", "Chalkboard"], ["image", "Image"]]}
            onChange={(v) => e((d) => {
              d.board.background.kind = v;
              if (v === "chalkboard" && d.board.background.color === "#ffffff") d.board.background.color = "#2e3b35";
              if (v !== "chalkboard" && d.board.background.color === "#2e3b35") d.board.background.color = "#ffffff";
            })}
          />
        </Row>
        {project.board.background.kind !== "image" && (
          <Row label="Colour">
            <Color value={project.board.background.color} onChange={(v) => e((d) => void (d.board.background.color = v))} />
          </Row>
        )}
        {project.board.background.kind === "chalkboard" && <p className="muted small">Chalkboard suits light-coloured drawings; dark outlines lose contrast.</p>}
        {project.board.background.kind === "image" && (
          <Row label="Image">
            <Select
              value={project.board.background.assetId ?? ""}
              options={[["", "Choose…"], ...project.assets.filter((a) => a.kind === "image").map((a) => [a.id, a.name] as [string, string])]}
              onChange={(v) => e((d) => void (d.board.background.assetId = v || null))}
            />
          </Row>
        )}
        <Row label="Frame rate">
          <Select value={String(project.output.fps)} options={[["30", "30 fps"], ["25", "25 fps"], ["60", "60 fps"]]} onChange={(v) => e((d) => void (d.output.fps = Number(v)))} />
        </Row>
        <NumberField label="End at" suffix="s" value={project.duration ?? 0} step={0.1} min={0} hint="0 = end of the last audio or element"
          onChange={(v) => e((d) => void (d.duration = v > 0 ? v : null))} />
      </section>
      <section>
        <h4>Camera</h4>
        <Row label="Auto-camera" hint="Frame each element while it draws (keys can be overridden one by one)">
          <input type="checkbox" checked={project.camera.auto} onChange={(ev) => e((d) => void (d.camera.auto = ev.target.checked))} />
        </Row>
        {project.camera.auto && (
          <>
            <NumberField label="Padding" value={project.camera.autoPadding} step={0.01} min={0} max={1} onChange={(v) => e((d) => void (d.camera.autoPadding = v))} />
            <NumberField label="Max zoom" suffix="×" value={project.camera.autoMaxZoom} step={0.05} min={1} max={4} onChange={(v) => e((d) => void (d.camera.autoMaxZoom = v))} />
          </>
        )}
        <button className="wide" onClick={() => addCameraKey(preview.cameraAt(useEditor.getState().time))}>Add camera key at playhead</button>
        <button className="wide" onClick={() => useEditor.getState().set({ panel: "spread" })}
          title="Give each scene its own place on the whiteboard, so the camera can pan between them">Spread scenes across the board…</button>
        <NumberField label="Board W" value={project.board.width} step={100} min={1920} digits={0} hint="Size of the virtual board the camera travels over"
          onChange={(v) => e((d) => void (d.board.width = v))} />
        <NumberField label="Board H" value={project.board.height} step={100} min={1080} digits={0} onChange={(v) => e((d) => void (d.board.height = v))} />
      </section>
      <section>
        <h4>Hand</h4>
        <Row label="Default">
          <Select value={project.defaults.handId} options={[["marker", "Marker"], ["none", "No hand"], ...hands.map((h) => [h.id, h.name] as [string, string])]}
            onChange={(v) => e((d) => void (d.defaults.handId = v))} />
        </Row>
      </section>
      <section>
        <h4>Captions</h4>
        <p className="muted small">{project.subtitles.length ? `${project.subtitles.length} timed cues from the voiceover.` : "No timed cues yet (scene captions are used)."}</p>
        <button className="wide" onClick={() => useEditor.getState().set({ panel: "subtitles" })}>
          {project.subtitles.length ? "Edit captions / download SRT…" : "Generate captions from voiceover…"}
        </button>
        <VocabularyField project={project} />
        <Row label="Burn in">
          <input type="checkbox" checked={project.captions.enabled} onChange={(ev) => e((d) => void (d.captions.enabled = ev.target.checked))} />
        </Row>
        {project.captions.enabled && (
          <>
            <NumberField label="Size" value={project.captions.size} step={1} min={16} max={120} digits={0} onChange={(v) => e((d) => void (d.captions.size = v))} />
            <Row label="Font">
              <Select value={project.captions.fontAssetId ?? ""} options={[["", "First font"], ...fonts.map((f) => [f.id, f.name] as [string, string])]}
                onChange={(v) => e((d) => void (d.captions.fontAssetId = v || null))} />
            </Row>
            <Row label="Text"><Color value={project.captions.color} onChange={(v) => e((d) => void (d.captions.color = v))} /></Row>
            <p className="muted small">Timed cues win; without them each scene's caption (scene inspector) is shown.</p>
          </>
        )}
      </section>
      <section>
        <h4>Background music ducking</h4>
        <Row label="Enabled">
          <input type="checkbox" checked={project.ducking.enabled} onChange={(ev) => e((d) => void (d.ducking.enabled = ev.target.checked))} />
        </Row>
        <NumberField label="Amount" suffix="dB" value={project.ducking.amountDb} step={0.5} max={0} min={-40} onChange={(v) => e((d) => void (d.ducking.amountDb = v))} />
        <NumberField label="Attack" suffix="s" value={project.ducking.attack} step={0.05} min={0} onChange={(v) => e((d) => void (d.ducking.attack = v))} />
        <NumberField label="Release" suffix="s" value={project.ducking.release} step={0.05} min={0} onChange={(v) => e((d) => void (d.ducking.release = v))} />
      </section>
      <section className="muted small">
        <p>Select an element on the stage or timeline to edit it.</p>
        <p>
          <kbd>Space</kbd> play · <kbd>←</kbd>/<kbd>→</kbd> frame · <kbd>Shift</kbd>+arrows 1 s · <kbd>Ctrl</kbd>+<kbd>Z</kbd> undo · <kbd>Del</kbd> delete · <kbd>Ctrl</kbd>+<kbd>D</kbd> duplicate
        </p>
      </section>
    </div>
  );
}

/** Words this project's speech recognition should expect, on top of the shared list (Admin). */
function VocabularyField({ project }: { project: Project }) {
  const joined = project.vocabulary.join(", ");
  const [text, setText] = useState(joined);
  useEffect(() => setText(joined), [joined]);
  const commit = () => {
    const terms = [...new Set(text.split(/[,\n]/).map((t) => t.trim()).filter(Boolean))];
    if (terms.join(", ") !== joined) useEditor.getState().edit((d) => void (d.vocabulary = terms));
  };
  return (
    <label className="vocab-field" title={"Names, acronyms and jargon said in the voiceover, spelled as captions should show them, separated by commas.\n'heard => meant' fixes a phrase the recognizer keeps getting wrong (e.g. in rival => and RII).\nUsed when finding cues, generating captions and changing a voice; regenerate captions to apply. Admins keep a shared list for every project."}>
      <span className="row-label">Words to recognize</span>
      <textarea rows={2} value={text} placeholder="MXC, airworthiness, Tyler" onChange={(ev) => setText(ev.target.value)} onBlur={commit} />
    </label>
  );
}

/** Swap an image (keeping timing and placement) or turn an import placeholder into an image. */
function ReplaceImage({ el, project }: { el: Element; project: Project }) {
  const analysis = useEditor((s) => s.analysis);
  const images = project.assets.filter((a) => a.kind === "image" || a.kind === "svg");
  if (images.length === 0) return null;
  const replace = (id: string) => {
    if (!id) return;
    const a = images.find((x) => x.id === id)!;
    const size = analysis[id]?.size ?? [a.width ?? 1000, a.height ?? 1000];
    useEditor.getState().edit((p) => {
      const d = p.elements.find((x) => x.id === el.id);
      if (!d) return;
      if (d.type === "image" && d.image) {
        d.image.assetId = id;
        return;
      }
      // placeholder → image fitted into the left two-thirds, drawn over the time it had
      d.type = "image";
      d.text = null;
      d.name = d.name.replace(/^Placeholder /, "") + " image";
      d.image = { assetId: id, brushScale: 1, groupOrder: null };
      d.transform = { ...d.transform, x: 633.6, y: 540, scale: +Math.min(1152 / size[0], 928.8 / size[1]).toFixed(4), rotation: 0 };
      d.enter = { ...d.enter, mode: "draw", outlineDur: 2.6, fillDur: 1.5 };
    });
  };
  return (
    <section>
      <h4>{el.image ? "Replace image" : "Replace placeholder"}</h4>
      <Row label="Image">
        <Select value={"" as string} options={[["", "Choose an image…"], ...images.map((a) => [a.id, a.name] as [string, string])]} onChange={replace} />
      </Row>
      {!el.image && <p className="muted small">Upload the scene's picture, then pick it here; the timing stays.</p>}
    </section>
  );
}

/** In a build scene: draw only what changed since the previous step's picture, or the whole image. */
function BuildStepControl({ el, project }: { el: Element; project: Project }) {
  const scene = project.scenes.find((s) => s.id === el.sceneId);
  const asset = project.assets.find((a) => a.id === el.image?.assetId);
  const st = useEditor((s) => (asset ? s.analysis[asset.id] : undefined));
  if (!scene || scene.startMode !== "build" || !asset?.analysis) return null;
  const against = asset.analysis.params.diffAgainst;
  const running = st?.job && ["queued", "running"].includes(st.job.status);
  const whole = async () => {
    useEditor.getState().edit((d) => void (d.assets.find((a) => a.id === asset.id)!.analysis!.params.diffAgainst = null));
    await flush();
    await api.analyze(project.id, asset.id, useEditor.getState().project!.assets.find((a) => a.id === asset.id)!.analysis!.params);
    window.dispatchEvent(new Event("studio:poll"));
  };
  return (
    <div className="build-step">
      {against ? (
        <>
          <p className="muted small">
            Draws only what changed since <code>{against.split("/").pop()}</code>
            {st?.diff ? ` (${Math.round(st.diff.changed * 100)}% of the picture)` : ""}.
          </p>
          <button className="wide" disabled={!!running} onClick={whole}>Draw the whole image instead</button>
        </>
      ) : (
        <button className="wide" disabled={!!running} onClick={() => queueDiffFor(el.id)} title="For a full picture of this build step: compare with the previous step and draw just the new parts, aligned on top">
          Draw only what's new since the previous step
        </button>
      )}
    </div>
  );
}

function MultiInspector({ preview, count }: { preview: Preview; count: number }) {
  const bounds = (id: string) => preview.boardBounds(id);
  const A = (how: Parameters<typeof align>[0], title: string, Icon: typeof AlignStartVertical) => (
    <button title={title} onClick={() => align(how, bounds)}><Icon size={16} /></button>
  );
  return (
    <div className="inspector">
      <div className="insp-head">
        <strong className="name">{count} elements</strong>
        <div className="icon-row">
          <button title="Duplicate (Ctrl+D)" onClick={duplicateSelection}><Copy size={15} /></button>
          <button title="Delete (Del)" onClick={deleteSelection}><Trash2 size={15} /></button>
        </div>
      </div>
      <section>
        <h4>Align</h4>
        <div className="align-row">
          {A("left", "Align left edges", AlignStartVertical)}
          {A("hcenter", "Align centres horizontally", AlignCenterVertical)}
          {A("right", "Align right edges", AlignEndVertical)}
          {A("top", "Align top edges", AlignStartHorizontal)}
          {A("vmiddle", "Align centres vertically", AlignCenterHorizontal)}
          {A("bottom", "Align bottom edges", AlignEndHorizontal)}
        </div>
        <h4>Distribute</h4>
        <div className="align-row">
          <button title="Equal horizontal gaps (3 or more)" disabled={count < 3} onClick={() => distribute("h", bounds)}><AlignHorizontalSpaceAround size={16} /></button>
          <button title="Equal vertical gaps (3 or more)" disabled={count < 3} onClick={() => distribute("v", bounds)}><AlignVerticalSpaceAround size={16} /></button>
        </div>
      </section>
      <section className="muted small">
        <p>Drag any of them on the stage or the timeline to move them together.</p>
        <p><kbd>Shift</kbd>+click adds/removes · drag on empty stage to box-select · <kbd>Ctrl</kbd>+<kbd>A</kbd> the whole scene · <kbd>Ctrl</kbd>+<kbd>C</kbd>/<kbd>X</kbd>/<kbd>V</kbd> copy/cut/paste at the playhead · <kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>V</kbd> paste in place</p>
      </section>
    </div>
  );
}
